import React, { useEffect, useState, useCallback } from 'react';
import { Check, X, AlertTriangle, Info } from 'lucide-react';

/**
 * Toast variant configurations
 * Each variant includes icon, colors, and accessibility label
 */
const TOAST_VARIANTS = {
  success: {
    Icon: Check,
    iconClass: 'text-success-400',
    borderClass: 'border-success-500/30',
    label: 'Success',
  },
  error: {
    Icon: X,
    iconClass: 'text-error-400',
    borderClass: 'border-error-500/30',
    label: 'Error',
  },
  warning: {
    Icon: AlertTriangle,
    iconClass: 'text-warning-400',
    borderClass: 'border-warning-500/30',
    label: 'Warning',
  },
  info: {
    Icon: Info,
    iconClass: 'text-secondary-400',
    borderClass: 'border-secondary-500/30',
    label: 'Information',
  },
};

/**
 * Individual Toast notification component
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Unique toast identifier
 * @param {string} props.message - Toast message content
 * @param {('success'|'error'|'warning'|'info')} props.type - Toast variant type
 * @param {number} props.duration - Auto-dismiss duration in ms (0 for no auto-dismiss)
 * @param {Function} props.onDismiss - Callback when toast is dismissed
 */
const Toast = ({ id, message, type = 'info', duration = 5000, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const variant = TOAST_VARIANTS[type] || TOAST_VARIANTS.info;
  const { Icon, iconClass, borderClass, label } = variant;

  /**
   * Handle toast dismissal with exit animation
   */
  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    // Wait for exit animation to complete before removing
    setTimeout(() => {
      onDismiss(id);
    }, 300);
  }, [id, onDismiss]);

  // Entrance animation on mount
  useEffect(() => {
    // Small delay to trigger CSS transition
    const showTimer = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => cancelAnimationFrame(showTimer);
  }, []);

  // Auto-dismiss timer
  useEffect(() => {
    if (duration <= 0) return;

    const dismissTimer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => clearTimeout(dismissTimer);
  }, [duration, handleDismiss]);

  return (
    <div
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      className={`
        relative flex items-start gap-3 p-4 rounded-lg
        glass border ${borderClass}
        shadow-lg shadow-black/20
        min-w-[320px] max-w-[420px]
        transform transition-all duration-300 ease-out
        ${isVisible && !isExiting
          ? 'translate-x-0 opacity-100'
          : 'translate-x-full opacity-0'
        }
      `}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 mt-0.5 ${iconClass}`}>
        <Icon size={20} aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <span className="sr-only">{label}: </span>
        <p className="text-sm text-white/90 leading-relaxed break-words">
          {message}
        </p>
      </div>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={handleDismiss}
        className="
          flex-shrink-0 p-1 rounded-md
          text-white/50 hover:text-white/90
          hover:bg-white/10
          transition-colors duration-150
          focus:outline-none focus:ring-2 focus:ring-white/20
        "
        aria-label="Dismiss notification"
      >
        <X size={16} aria-hidden="true" />
      </button>

      {/* Progress bar for auto-dismiss */}
      {duration > 0 && (
        <div
          className="absolute bottom-0 left-0 h-0.5 bg-white/20 rounded-b-lg"
          style={{
            animation: `toast-progress ${duration}ms linear forwards`,
          }}
        />
      )}

      {/* Inject keyframes for progress animation */}
      <style>{`
        @keyframes toast-progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

export default Toast;
